/*  MUI  */
import { Button, TableCell } from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { getDateTime } from 'application/Shared/Components/Core';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Route, Transition } from 'application/Shared/Helpers';
import { Modal, ModalBody, ModalFooter } from 'application/Shared/Modals';

/*  Objects  */
import { Application, ApplicationBusinessOwner } from 'objects/Application';
import {
    Contract,
    ContractAlertingIconWithText,
    ContractDate,
    ContractTCV,
    ContractType
} from 'objects/Contract';

/*  React  */
import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const headers = (t) => [
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>{t('Common.application')}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'application.name',
        tooltip: 'application',
        width: '20%'
    },
    {
        align: 'left',
        field: 'business_owner',
        renderHeader: <strong>{t('Common.business_owner')}</strong>,
        tooltip: 'business_owner',
        width: '20%'
    },
    {
        align: 'left',
        field: 'alerting',
        renderHeader: <strong>{t('Common.alerting')}</strong>,
        width: '10%'
    },
    {
        align: 'left',
        field: 'type',
        renderHeader: <strong>{t('Common.type')}</strong>,
        tooltip: 'contract_management_monthly_renewal_date',
        width: '20%'
    },
    {
        align: 'left',
        field: 'renewalDate',
        renderHeader: <strong>{t('Common.contract_date')}</strong>,
        tooltip: 'renewalDate',
        width: '15%'
    },
    {
        align: 'left',
        field: 'tcv',
        renderHeader: <strong>{t('Common.tcv')}</strong>,
        tooltip: 'contract_management_tcv',
        width: '15%'
    }
];

const searchFields = [
    'application.name',
    'application.category',
    'application.subCategory'
];

const RenderRow = ({ abbreviatedMonthDate, row }) => {
    const { clientId, partnerId } = useParams();
    const baseAdminRoute = Route.buildBaseAdminRoute({ clientId, partnerId });

    return (
        <Application
            data={row.application}
            nameLinkTo={`${baseAdminRoute}/application/${row.application.id}/contract`}
        >
            <TableCell>
                <ApplicationNameLinkCell withCategory />
            </TableCell>
            <TableCell>
                <ApplicationBusinessOwner />
            </TableCell>
            <Contract data={row}>
                <TableCell>
                    <ContractAlertingIconWithText />
                </TableCell>
                <TableCell>
                    <ContractType />
                </TableCell>
                <TableCell>
                    <ContractDate abbreviatedMonthDate={abbreviatedMonthDate} />
                </TableCell>
                <TableCell>
                    <ContractTCV />
                </TableCell>
            </Contract>
        </Application>
    );
};

export const ContractListForSpecificDayModal = ({
    abbreviatedMonthDate,
    closeDrilldownVisualModal,
    drillDownDate,
    drillDownEvents,
    drilldownVisual
}) => {
    const { t } = useTranslation();

    const tableHeaders = useMemo(() => headers(t), [t]);

    return (
        <Modal
            data-testid="contractListForSpecificDayModal"
            open={drilldownVisual}
            style={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center'
            }}
            onClose={() => closeDrilldownVisualModal()}
            maxWidth="xl"
            fullWidth
            role="dialog"
            scroll="paper"
            aria-labelledby="contracts-up-for-renewal"
            TransitionComponent={Transition}
            keepMounted
            title={
                <Trans
                    i18nKey="ApplicationContractManagement.contractsUpForReviewOn"
                    values={{
                        date: getDateTime({
                            abbreviatedMonthDate,
                            date: drillDownDate,
                            noTimeString: true
                        })
                    }}
                />
            }
        >
            <>
                <ModalBody dividers>
                    <ManagedTable
                        headers={tableHeaders}
                        renderBody={(row) => (
                            <RenderRow
                                abbreviatedMonthDate={abbreviatedMonthDate}
                                row={row}
                            />
                        )}
                        renderBodyKey="id"
                        renderFooter
                        rows={drillDownEvents}
                        rowsPerPage={5}
                        searchEnabled
                        searchFields={searchFields}
                        sortDirection="ASC"
                        sortFieldType="string"
                        sortKey="application.name"
                    />
                </ModalBody>
                <ModalFooter>
                    <Button
                        data-testid="closeModalBtn"
                        data-trackid="admin_contracts_contractList_close"
                        onClick={() => closeDrilldownVisualModal()}
                        color="primary"
                        variant="contained"
                    >
                        {t('Common.close')}
                    </Button>
                </ModalFooter>
            </>
        </Modal>
    );
};
